import { atom, Getter } from 'jotai';
import { isEqual } from 'lodash-es';
import {
  mapRefAtoms,
  mapBoundsAtoms,
  mapFilterModeAtoms,
  spatialFilterDisabledAtoms,
  effectiveMapFilterModeAtoms,
  aoiPolygonAtoms,
  aoiPolygonLockedAtoms,
  aoiDrawingAtoms,
  unitShapeEditingAtoms,
  unitShapeEditRequestedAtoms,
  unitShapeWorkingRingAtoms,
  mapGesturesSuspendedAtoms,
} from '../inventory-surface/map-atoms';
import {
  DEFAULT_CROSS_PLOT_CHART,
  TC_WELL_FILTER_CATEGORIES,
  TC_UNIT_FILTER_CATEGORIES,
  omitDisabledCategories,
} from './utils';
import { tcEffectiveWorkspaceIsPromotedAtom } from 'components/type-curve-workspace/atoms';
import type { SavedWellFilter } from 'types/type-curve-workspace-show';
import type { SurveyRow } from 'components/survey-description';
import type { Pieces } from 'components/geometry-editing';
import type {
  CrossPlotChartConfig,
  ChartSeriesVisibility,
  RatiosChartVisibility,
  PageSectionKey,
  ColorByTarget,
  FilteredWell,
  TcWellsFacets,
  TcWellsStatusCounts,
  ColorByConfig,
  TcWellFilters,
  TcUnitFilters,
  TcUnitsFacets,
} from '../types';
import type {
  FilteredUnit,
  DevelopmentAreaOverlappingResult,
  UnitOverride,
  UnitSpacingConfig,
} from 'types/api-responses';

// Wraps a derived atom so it returns a stable reference when the
// computed value is structurally equal to the previous one.
// Prevents downstream re-renders when only disabled rules change.
function stableAtom<T>(readFn: (get: Getter) => T) {
  let prev: T | undefined;
  return atom<T>((get) => {
    const next = readFn(get);
    if (prev !== undefined && isEqual(prev, next)) return prev;
    prev = next;
    return next;
  });
}

// ─── Page Section Visibility ───

export const pageSectionVisibilityAtom = atom<Record<PageSectionKey, boolean>>({
  typeCurveDetails: true,
  inventoryDetails: true,
  summaryTable: true,
  map: true,
  dcaParameters: true,
  charts: true,
  inventoryManagement: true,
  unitsTable: true,
});

// ─── Empty State (no Type Curve Area) ───
// Set by the page from its effective header: `null` = loading/unknown,
// `false` = empty state (header has no typeCurveAreaId), `true` = has area.
// Consumers that already hold the effective header derive locally instead of
// reading this; this atom is for consumers where the header is not in scope.
export const tcHasTypeCurveAreaAtom = atom<boolean | null>(null);

// Sidebar sections forced off in the empty state (the 5 data-driven sections).
// `map` and `unitsTable` are intentionally left at their stored value.
export const EMPTY_STATE_HIDDEN_SECTIONS: PageSectionKey[] = [
  'typeCurveDetails',
  'summaryTable',
  'dcaParameters',
  'inventoryManagement',
  'charts',
];

// Effective section visibility: the stored user intent, gated by the empty
// state. Only when `tcHasTypeCurveAreaAtom === false` (explicit empty state, not
// loading) are the 5 sections forced off — the stored intent is untouched, so
// selections auto-restore once an area appears.
export const effectivePageSectionVisibilityAtom = atom<
  Record<PageSectionKey, boolean>
>((get) => {
  const visibility = get(pageSectionVisibilityAtom);
  if (get(tcHasTypeCurveAreaAtom) !== false) return visibility;

  const gated = { ...visibility };
  for (const key of EMPTY_STATE_HIDDEN_SECTIONS) gated[key] = false;
  return gated;
});

// ─── Chart Series Visibility ───

export interface CrossPlotChartVisibility {
  crossPlot: boolean;
}

export const probitPlotChartAtom = atom<ChartSeriesVisibility>({
  oil: true,
  gas: true,
  water: true,
});

export const monthlyVolumesChartAtom = atom<ChartSeriesVisibility>({
  oil: true,
  gas: true,
  water: true,
});

export const ratiosChartAtom = atom<RatiosChartVisibility>({
  gor: true,
  wor: true,
  wgr: true,
  cgr: true,
  watercut: true,
});

export const crossPlotChartAtom = atom<CrossPlotChartVisibility>({
  crossPlot: true,
});

export const tcCrossPlotChartsAtom = atom<CrossPlotChartConfig[]>([
  { ...DEFAULT_CROSS_PLOT_CHART },
]);

/** Modal open state for "Add / Edit Cross Plot Chart". */
export const tcCrossPlotChartModalOpenAtom = atom(false);

/**
 * When set, the modal opens in edit mode pre-filled with the chart's config.
 * `null` means "create a new chart".
 */
export const tcEditingCrossPlotChartIdAtom = atom<string | null>(null);

// ─── Map interaction (per-surface) ───
// These map atoms are now per-surface — the inventory page mounts a Main-Page map
// and a Unit-Builder map at once, and they must not share ref / viewport / filter
// mode / AOI / shape-edit state (see `inventory-surface/map-atoms.ts` for why).
// The names below are the MAIN surface, re-exported so every existing main-page
// and plain-Type-Curve consumer is untouched. Components that render inside BOTH
// maps (`TypeCurveMap`, the dev-areas / AOI / shape-editor layers) instead read
// `…Atoms[useSurface()]`.
export const tcMapFilterModeAtom = mapFilterModeAtoms.main;
export const tcMapRefAtom = mapRefAtoms.main;
export const tcAoiPolygonAtom = aoiPolygonAtoms.main;
export const tcAoiPolygonLockedAtom = aoiPolygonLockedAtoms.main;
export const tcSpatialFilterDisabledAtom = spatialFilterDisabledAtoms.main;
export const tcEffectiveMapFilterModeAtom = effectiveMapFilterModeAtoms.main;
export const tcUnitShapeEditingAtom = unitShapeEditingAtoms.main;
export const tcUnitShapeEditRequestedAtom = unitShapeEditRequestedAtoms.main;
export const tcUnitShapeWorkingRingAtom = unitShapeWorkingRingAtoms.main;
export const tcAoiDrawingAtom = aoiDrawingAtoms.main;
export const tcMapGesturesSuspendedAtom = mapGesturesSuspendedAtoms.main;

// ─── Map Bounds (for visible-extent mode) ───

export const tcMapBoundsAtom = mapBoundsAtoms.main;

// ─── Shared Wells Data ───

export const tcWellsDataAtom = atom<any[]>([]);
export const tcWellsTotalCountAtom = atom<number>(0);
export const tcWellsLoadingAtom = atom<boolean>(false);

// ─── Filtered UWIs (all matching wells from server, regardless of pagination) ───
// Each entry carries `uwi` + `name` + `wellQualifier` so downstream views
// (e.g. probit tooltips) don't need a separate wells-table lookup.

export const tcFilteredUwisAtom = atom<FilteredWell[]>([]);

// ─── Server Facets ───

export const tcWellsFacetsAtom = atom<TcWellsFacets | null>(null);

// Per-status counts for the wells the table is currently listing — the same set,
// the same filters, so they add up to `tcWellsTotalCountAtom`. Deliberately not
// read off `tcWellsFacetsAtom.status`: a facet self-excludes its own filter so
// the sidebar stays multi-selectable, which means the moment a status filter is
// on, its buckets describe a larger set than the table.
//
// Null until the first wells response lands.
export const tcWellsStatusCountsAtom = atom<TcWellsStatusCounts | null>(null);

// ─── Color By UWI Map (from independent color-by fetches) ───

export const tcColorByUwiMapAtom = atom<Record<string, string[]>>({});
export const tcColorByLoadingAtom = atom(false);

// ─── Well Filter Modal ───

export const tcWellFilterModalOpenAtom = atom(false);

// Category the filter modal should open on — set by an applied filter's edit
// icon so it lands on that filter. Null (the "Add Filter" path) opens on the
// first category.
export const tcWellFilterModalCategoryAtom = atom<string | null>(null);

export const tcWellFiltersAtom = atom<TcWellFilters>({});
export const tcPendingWellFiltersAtom = atom<TcWellFilters>({});

// Inventory Workspace only: bound the wells query by the selected zone's UNITS
// rather than by the Type Curve Area polygon, and match the shape against the
// perforated lateral rather than the whole directional survey.
//
// Set from the zone-details response (`unitScopeGeojson`), which is the dissolved
// footprint of the very units the zone's slots are generated from. Without it the
// Wells section and Property Counts by Status are bounded by different regions:
// the member units neither tile the Type Curve Area nor stay inside it, so the
// section counted wells no unit contains (58 of 197 on one Midland zone) while
// missing wells the inventory holds.
//
// Null on the plain Type Curve pages and while the zone details are loading, so
// they keep the area-polygon + survey behaviour untouched.
export const tcInventoryWellScopeAtom = atom<{
  unitScopeGeojson: GeoJSON.MultiPolygon | GeoJSON.Polygon;
} | null>(null);

// ─── Saved (persisted) Wells Filters ───
// Read mirror of the React-Query list cache (written by useSavedWellFiltersQuery).
// One row per property/category (name = category id). Source of the category→id
// map for POST/PATCH/DELETE syncing and of the ids cited as verification-task
// evidence.
export const tcSavedWellFiltersAtom = atom<SavedWellFilter[]>([]);

// Category ids the user has switched off. Session-only — the filter keeps its
// definition (and its saved row) and simply stops narrowing the queries.
export const tcDisabledWellFilterCategoriesAtom = atom<string[]>([]);

// tcWellFiltersAtom with the disabled categories' params stripped — what every
// wells query should send. The raw atom stays the edit/persist source of truth.
export const tcEnabledWellFiltersAtom = atom<TcWellFilters>((get) =>
  omitDisabledCategories(
    get(tcWellFiltersAtom),
    get(tcDisabledWellFilterCategoriesAtom),
    TC_WELL_FILTER_CATEGORIES
  )
);

// ─── Unit Filter Modal (Development Areas) ───

export const tcUnitFilterModalOpenAtom = atom(false);

// Units counterpart of tcWellFilterModalCategoryAtom.
export const tcUnitFilterModalCategoryAtom = atom<string | null>(null);

export const tcUnitFiltersAtom = atom<TcUnitFilters>({});
export const tcPendingUnitFiltersAtom = atom<TcUnitFilters>({});

// Units counterparts of the wells disabled/enabled pair above.
export const tcDisabledUnitFilterCategoriesAtom = atom<string[]>([]);

export const tcEnabledUnitFiltersAtom = atom<TcUnitFilters>((get) =>
  omitDisabledCategories(
    get(tcUnitFiltersAtom),
    get(tcDisabledUnitFilterCategoriesAtom),
    TC_UNIT_FILTER_CATEGORIES
  )
);

export const tcUnitsFacetsAtom = atom<TcUnitsFacets | null>(null);
export const tcUnitsTotalCountAtom = atom<number>(0);

export const tcFilteredUnitsAtom = atom<FilteredUnit[]>([]);
export const tcOverlappingUnitsAtom = atom<DevelopmentAreaOverlappingResult[]>(
  []
);

/**
 * Inventory Workspace only: the zone (inventory formation package) whose setback
 * outline the dev-areas tile draws. The tile emits a `setback` feature per unit
 * only when it is told which zone measures it, since a unit sits under several
 * zones and each can carry its own setbacks. `null` emits no outline, which is
 * what every plain Type Curve page gets.
 */
export const tcSetbackZoneIdAtom = atom<number | null>(null);

/**
 * Inventory Workspace only: scopes the gun barrel to the inventory backend. When
 * set, the `/queries/gun_barrel` request sends the `inventory_unit_slots` layer
 * (all configured zones in one call) + `subject.inventoryWorkspaceId` and omits
 * the legacy `insights_unit_formation_slots` layer + `targetSpacingFt`; the parse
 * picks the selected zone by `inventoryFormationPackageId`. `null` on the plain
 * Type Curve page, so its gun barrel is unchanged.
 */
export const tcInventoryGunBarrelScopeAtom = atom<{
  inventoryWorkspaceId: number;
  inventoryFormationPackageId: number | null;
  // The selected/default package id of every formation the workspace covers,
  // across ALL type curve areas shown in the left sidebar (one per formation).
  // The gun barrel + slot table scope to all of these (not just the selected
  // zone); the map slot layer uses the single `inventoryFormationPackageId` above.
  inventoryFormationPackageIds: number[];
} | null>(null);

/**
 * True where filters / color-by / cross-plots apply to the current view only and are never saved:
 *
 * - The Inventory Workspace, which does not save them.
 * - The Type Curve Details page and any promoted workspace. Both read a published workspace, whose
 *   filters stay as they were when it was promoted and are shared by every user.
 *
 * An unpromoted workspace is the one place the filters are still being worked on, so it saves.
 */
export const tcFiltersLocalOnlyAtom = atom(
  (get) =>
    get(tcInventoryGunBarrelScopeAtom) !== null ||
    get(tcEffectiveWorkspaceIsPromotedAtom)
);

/**
 * Inventory Workspace only: the formation NAME whose band the gun barrel should
 * highlight — the selected sidebar leaf's formation. `null` = no highlight.
 */
export const tcHighlightedFormationAtom = atom<string | null>(null);

/**
 * Inventory Workspace only: gates the map's development-areas and slot-geometries
 * layers behind the FOUCS exclusions recompute (`POST .../foucs_exclusions`) that
 * the page fires after the workspace loads. Those layers render units/slots whose
 * in/out-of-scope status the recompute decides, so they must not fetch until it
 * returns 200.
 *
 * `null` = ungated (plain Type Curve pages behave exactly as before). Non-null =
 * the Inventory Workspace page is driving the gate: `'pending'` while the POST is
 * in flight, `'ready'` once it returned 200 (layers may fetch), `'error'` on a
 * non-200 / transport failure (layers stay held). Layers treat anything other
 * than `null` or `'ready'` as blocked.
 */
export type TcFoucsGateStatus = 'pending' | 'ready' | 'error';
export const tcInventoryFoucsGateAtom = atom<TcFoucsGateStatus | null>(null);

/**
 * Inventory Workspace only: the selected type-curve area's formations (ordered as
 * the sidebar tree renders them), each with its backend swatch colour. Drives the
 * Formation options in the shared Well-Filter and Color-by pickers, and the
 * default Color-by colour per formation. `null` on plain Type Curve pages, so
 * those pickers fall back to the server facets and the default palette.
 */
export const tcInventoryAreaFormationsAtom = atom<
  { formation: string; color: string }[] | null
>(null);

/**
 * Inventory Workspace only: the selected zone's read-only spacing parameters,
 * mirrored from the Inventory Details section's `InventoryTypeCurveDetailsResponse`
 * so the gun barrel controls can show them in place of the editable Target
 * Spacing input. `null` on the plain Type Curve page (which keeps the input).
 */
export const tcInventorySpacingAtom = atom<{
  wellSpacingFt: number | null;
  crossLineSetbackFt: number | null;
  inLineSetbackFt: number | null;
  spacingToleranceFt: number | null;
} | null>(null);

/**
 * Per-zone spacing configs for the currently-scoped unit, from
 * `GET …/units/:unitId/spacing_configs` (one row per formation package).
 * Published by the gun barrel (which fetches it, on both the main page and the
 * Unit Builder) so the builder's sibling spacing controls can seed the selected
 * formation's values without re-fetching. `null` until loaded / off an inventory
 * surface / on a draft unit.
 */
export const tcUnitSpacingConfigsAtom = atom<UnitSpacingConfig[] | null>(null);

/** The four spacing/setback values a generation run is laid out against. */
export interface InventorySpacingValues {
  wellSpacingFt: number | null;
  crossLineSetbackFt: number | null;
  inLineSetbackFt: number | null;
  spacingToleranceFt: number | null;
}

/**
 * True when there are no remaining overlapping units to resolve. The backend
 * excludes resolved (removed) units from `overlappingResults`, so an empty list
 * means every overlap has been resolved (or there were none). Read by the LTD
 * generation buttons, which still wait for a clear map. The Resolve unit
 * overlaps verdict does not wait: it saves with overlaps still open.
 */
export const tcAllOverlapsResolvedAtom = atom(
  (get) => get(tcOverlappingUnitsAtom).length === 0
);

/**
 * Development-areas request in flight. Starts `true` because an empty
 * `tcOverlappingUnitsAtom` is indistinguishable between "no overlaps" and "no
 * response yet", so anything acting on an empty overlap list has to rule out an
 * in-flight fetch first. Mirrors the local `isLoading` in useDevelopmentAreasQuery.
 */
export const tcUnitsLoadingAtom = atom<boolean>(true);

// ─── Color By Attribute Modal ───

export const tcColorByModalOpenAtom = atom(false);

// Starts empty. The default Well Qualifier rule is injected only in a promoted
// context (usePromotedContextDefaults) — a blank workspace has no colour rules.
export const tcColorByRulesAtom = atom<ColorByConfig[]>([]);
export const tcColorByConfigAtom = tcColorByRulesAtom;

export const tcUwiColorMapAtom = stableAtom<Record<string, string>>((get) => {
  const rules = get(tcColorByRulesAtom);
  const colorByUwiMap = get(tcColorByUwiMapAtom);
  const mapRules = rules.filter((r) => r.enabled && r.applyTo.map);
  if (mapRules.length === 0) return {};

  const uwiColorMap: Record<string, string> = {};
  for (const rule of mapRules) {
    for (const value of rule.selectedValues) {
      const color = rule.valueColors[value];
      if (!color) continue;
      const key = `${rule.attribute}:${value}`;
      const uwis = colorByUwiMap[key];
      if (!uwis) continue;
      for (const uwi of uwis) {
        uwiColorMap[uwi] = color; // Last rule wins
      }
    }
  }
  return uwiColorMap;
});
export const tcProbitColorMapAtom = stableAtom<Record<string, string>>(
  (get) => {
    const rules = get(tcColorByRulesAtom);
    const colorByUwiMap = get(tcColorByUwiMapAtom);
    const probitRules = rules.filter((r) => r.enabled && r.applyTo.probitPlot);
    if (probitRules.length === 0) return {};

    const uwiColorMap: Record<string, string> = {};
    for (const rule of probitRules) {
      for (const value of rule.selectedValues) {
        const color = rule.valueColors[value];
        if (!color) continue;
        const key = `${rule.attribute}:${value}`;
        const uwis = colorByUwiMap[key];
        if (!uwis) continue;
        for (const uwi of uwis) {
          uwiColorMap[uwi] = color;
        }
      }
    }
    return uwiColorMap;
  }
);

export const tcMonthlyVolumesColorMapAtom = stableAtom<Record<string, string>>(
  (get) => {
    const rules = get(tcColorByRulesAtom);
    const colorByUwiMap = get(tcColorByUwiMapAtom);
    const volumesRules = rules.filter(
      (r) => r.enabled && r.applyTo.monthlyVolumes
    );
    if (volumesRules.length === 0) return {};

    const uwiColorMap: Record<string, string> = {};
    for (const rule of volumesRules) {
      for (const value of rule.selectedValues) {
        const color = rule.valueColors[value];
        if (!color) continue;
        const key = `${rule.attribute}:${value}`;
        const uwis = colorByUwiMap[key];
        if (!uwis) continue;
        for (const uwi of uwis) {
          uwiColorMap[uwi] = color;
        }
      }
    }
    return uwiColorMap;
  }
);

export const tcRatiosColorMapAtom = stableAtom<Record<string, string>>(
  (get) => {
    const rules = get(tcColorByRulesAtom);
    const colorByUwiMap = get(tcColorByUwiMapAtom);
    const ratiosRules = rules.filter((r) => r.enabled && r.applyTo.ratios);
    if (ratiosRules.length === 0) return {};

    const uwiColorMap: Record<string, string> = {};
    for (const rule of ratiosRules) {
      for (const value of rule.selectedValues) {
        const color = rule.valueColors[value];
        if (!color) continue;
        const key = `${rule.attribute}:${value}`;
        const uwis = colorByUwiMap[key];
        if (!uwis) continue;
        for (const uwi of uwis) {
          uwiColorMap[uwi] = color;
        }
      }
    }
    return uwiColorMap;
  }
);

export const tcCrossPlotColorMapAtom = stableAtom<Record<string, string>>(
  (get) => {
    const rules = get(tcColorByRulesAtom);
    const colorByUwiMap = get(tcColorByUwiMapAtom);
    const crossPlotRules = rules.filter(
      (r) => r.enabled && r.applyTo.crossPlot
    );
    if (crossPlotRules.length === 0) return {};

    const uwiColorMap: Record<string, string> = {};
    for (const rule of crossPlotRules) {
      for (const value of rule.selectedValues) {
        const color = rule.valueColors[value];
        if (!color) continue;
        const key = `${rule.attribute}:${value}`;
        const uwis = colorByUwiMap[key];
        if (!uwis) continue;
        for (const uwi of uwis) {
          uwiColorMap[uwi] = color;
        }
      }
    }
    return uwiColorMap;
  }
);

// ─── Selected Development Area ───

export interface TcSelectedDevArea {
  developmentAreaId: number;
  developmentAreaName: string;
  azimuthDeg?: number;
  centroidInTypeCurve?: boolean;
  crossSectionEndPoints?: {
    start: { lat: number; lon: number };
    end: { lat: number; lon: number };
  };
  /**
   * The unit's footprint as a closed `[lon, lat]` ring, derived from its axis
   * frame. Seeds the shape editor: tile geometry is clipped at tile edges and
   * derived units carry no server geometry, so the frame is the only complete
   * description available on the client.
   */
  ring?: GeoJSON.Position[];
  /**
   * For a client-side clone: the real development area it was copied from.
   *
   * A clone's own `developmentAreaId` is synthetic and negative, so any endpoint
   * keyed on it fails. Server-side reads that a draft can legitimately share with
   * its source — the gun barrel above all, since a fresh clone occupies the same
   * ground — resolve through this instead. Absent on a real selection.
   */
  sourceDevelopmentAreaId?: number;
}

/** Full selection state — set by the map layer on click or external navigation. */
export const tcSelectedDevAreaAtom = atom<TcSelectedDevArea | null>(null);

/**
 * Write a dev-area name here to ask the map layer to select it.
 * The layer component processes the request (zoom, highlight, compute endpoints),
 * writes the result to `tcSelectedDevAreaAtom`, and clears this atom.
 */
export const tcSelectDevAreaRequestAtom = atom<string | null>(null);

// ─── Cloned units (Unit Builder, frontend-only) ───

/**
 * A client-only cloned unit — the mock's "Clone unit". Never persisted: lives in
 * memory for the session and disappears on reload. Its `selection.ring` reseeds
 * the shape editor when the clone is (re)selected, and its `surveyRows`/`pieces`
 * are its independent working copy so edits never touch the source unit.
 */
export interface ClonedUnit {
  /** Synthetic NEGATIVE id — real dev-area ids are always positive, so no clash. */
  id: number;
  /** `"<source> (copy)"`; also how a clone is recognized in the navigator/resolver. */
  name: string;
  /** Full selection snapshot; `ring` seeds the boundary on (re)select. */
  selection: TcSelectedDevArea;
  /** Independent copy of the source's survey rows, restored on select. */
  surveyRows: SurveyRow[];
  /** The clone's live boundary working copy, kept current by the write-back effect. */
  pieces: Pieces;
}

/** Every clone made this session. Ephemeral — empty on each page load. */
export const clonedUnitsAtom = atom<ClonedUnit[]>([]);

/**
 * Whether the currently-selected unit is a session clone rather than a persisted
 * unit. A clone has a synthetic negative `developmentAreaId` and no server record,
 * so membership in `clonedUnitsAtom` is the reliable signal — used by the Unit
 * Builder's Save to enable "save this clone as a new unit", and by
 * `builderScopedUnitAtom` to scope a clone as a draft (`unitId: null`).
 */
export const tcSelectedIsCloneAtom = atom((get) => {
  const selected = get(tcSelectedDevAreaAtom);
  if (!selected) return false;
  return get(clonedUnitsAtom).some(
    (clone) => clone.id === selected.developmentAreaId
  );
});

/**
 * The unit stepper's list: the server's filtered units plus any clones, in
 * insertion order. Derived (not a mutation of `tcFilteredUnitsAtom`, which the
 * units hook overwrites on every refetch) so clones survive refetches.
 */
export const tcNavigableUnitsAtom = atom<FilteredUnit[]>((get) => {
  const clones = get(clonedUnitsAtom);
  const units = get(tcFilteredUnitsAtom);
  return clones.length === 0
    ? units
    : [
        ...units,
        ...clones.map((clone) => ({ id: clone.id, name: clone.name })),
      ];
});

// ─── Unit Color By Attribute ───

export const tcUnitColorByModalOpenAtom = atom(false);

export const tcUnitColorByRulesAtom = atom<ColorByConfig[]>([]);

/** Maps `"attribute:value"` → unit name arrays (like tcColorByUwiMapAtom but for unit names). */
export const tcUnitColorByNameMapAtom = atom<Record<string, string[]>>({});

/** Derived: flattens unit color-by rules + name map into `Record<unitName, color>`. */
export const tcUnitNameColorMapAtom = stableAtom<Record<string, string>>(
  (get) => {
    const rules = get(tcUnitColorByRulesAtom);
    const nameMap = get(tcUnitColorByNameMapAtom);
    const mapRules = rules.filter((r) => r.enabled && r.applyTo.map);
    if (mapRules.length === 0) return {};

    const unitNameColorMap: Record<string, string> = {};
    for (const rule of mapRules) {
      for (const value of rule.selectedValues) {
        const color = rule.valueColors[value];
        if (!color) continue;
        const key = `${rule.attribute}:${value}`;
        const names = nameMap[key];
        if (!names) continue;
        for (const name of names) {
          unitNameColorMap[name] = color; // Last rule wins
        }
      }
    }
    return unitNameColorMap;
  }
);

export const tcPendingColorByAtom = atom<{
  selectedValues: string[];
  applyTo: Record<ColorByTarget, boolean>;
}>({
  selectedValues: [],
  applyTo: {
    map: false,
    probitPlot: false,
    monthlyVolumes: false,
    ratios: false,
    crossPlot: false,
  },
});

// ─── Unit Resolution (Story 3) ───

/** Source of truth — unit overrides (remove/rotate) for the active workspace. */
export const unitOverridesAtom = atom<UnitOverride[]>([]);

/**
 * Derived: dev-area ids removed in this workspace. The map layer reads this to
 * hide removed units client-side (the MVT polygons themselves are unchanged).
 */
export const removedUnitIdsAtom = atom<Set<number>>(
  (get) =>
    new Set(
      get(unitOverridesAtom)
        .filter((o) => o.action === 'remove')
        .map((o) => o.unitId)
    )
);

/**
 * Bumped after any override mutation to drive the refresh fan-out — the
 * dev-areas query and comments panel watch this counter and re-fetch when it
 * changes (the override list itself is refreshed directly into unitOverridesAtom).
 */
export const tcUnitResolutionRefreshAtom = atom(0);

/**
 * True from the moment a remove/rotate/undo mutation is fired until the
 * consequent dev-areas re-fetch settles — i.e. until the map + overlap panel
 * actually reflect the change. Drives the loaders in the resolve panel and the
 * toolbar so the user gets feedback across the (1–2s) refresh gap, not just
 * while the mutation request itself is in flight.
 */
export const tcUnitResolutionPendingAtom = atom(false);

/**
 * True while a rotate / un-rotate action is in flight. Kept separate from
 * `tcUnitResolutionPendingAtom` because rotate triggers no dev-areas re-fetch:
 * it's cleared deterministically by the mutation itself (once the action list
 * re-GET applies the new direction), not by the units fetch's `finally` — which
 * fires on any units fetch and would otherwise clear it early. Drives the same
 * map + toolbar loader so the user gets feedback across the rotate round-trip.
 */
export const tcUnitRotationPendingAtom = atom(false);

/**
 * The LTD-direction bearing (degrees) the server last STATED for a unit this
 * session, keyed by `azimuthOverrideKey(workspaceId, unitId)`: the rotate
 * action's bearing after a rotate, the detected bearing after an un-rotate or a
 * restore.
 *
 * The map layers draw this as an absolute. Each map compares it with whatever
 * ITS OWN tiles bake for the unit and turns the frame by the difference, so a
 * map whose tiles predate the action and a map whose tiles already carry it are
 * both right, and the main page and the Unit Builder (which fetches fresh tiles
 * on every open) can never disagree. Once a tile re-request bakes the stated
 * bearing the difference is zero and the entry is inert. No entry means the
 * tile's baked bearing stands. The earlier design kept "rotated / un-rotated
 * this session" sets and nudged ±90 off whatever the tile showed; it drifted the
 * moment the two maps held tiles of different ages.
 */
export const unitAzimuthOverridesAtom = atom<Map<string, number>>(
  new Map<string, number>()
);

/**
 * id → lean unit details (acreage / unitName / unitType), populated and merged
 * across pages by the dev-areas query. Read by the resolve-overlap drawer to
 * show per-unit area + overlap acreage.
 */
export interface TcUnitDetail {
  name: string;
  unitName: string | null;
  unitType: string;
  acreage: number;
}
export const tcUnitDetailsByIdAtom = atom<Record<number, TcUnitDetail>>({});
